import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { useWebAuth } from '../../../hooks/useWebAuth';
import { getAdminUsers, blockUser, unblockUser } from '../../../api/admin-api';
import { AdminTable } from '../components/AdminTable';
import './UsersPage.scss';

interface AdminUser {
  id: string;
  email?: string;
  authProvider: string;
  plan: string;
  sharesCount: number;
  lastActiveAt?: string;
  createdAt: string;
  status: string;
}

const columnHelper = createColumnHelper<AdminUser>();

export function UsersPage() {
  const { token } = useWebAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminUser[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [showAnonymous, setShowAnonymous] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(
    (p: number, f: Record<string, string> = filters) => {
      if (!token) return;
      setLoading(true);
      const params = { page: String(p), limit: '20', ...f };
      if (showAnonymous) params.showAnonymous = 'true';
      getAdminUsers(token, params)
        .then((res) => {
          setData(res.data);
          setTotalPages(res.totalPages);
          setPage(res.page);
        })
        .finally(() => setLoading(false));
    },
    [token, filters, showAnonymous],
  );

  useEffect(() => {
    fetchUsers(1);
  }, [fetchUsers]);

  const handleBlock = async (userId: string) => {
    if (!token) return;
    await blockUser(token, userId);
    fetchUsers(page);
  };

  const handleUnblock = async (userId: string) => {
    if (!token) return;
    await unblockUser(token, userId);
    fetchUsers(page);
  };

  const columns = [
    columnHelper.accessor('email', {
      header: 'Email',
      cell: (info) => info.getValue() ?? 'Anonymous',
    }),
    columnHelper.accessor('authProvider', { header: 'Auth Provider' }),
    columnHelper.accessor('plan', { header: 'Plan' }),
    columnHelper.accessor('sharesCount', { header: 'Shares' }),
    columnHelper.accessor('lastActiveAt', {
      header: 'Last Active',
      cell: (info) => {
        const val = info.getValue();
        return val ? new Date(val).toLocaleDateString() : '-';
      },
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created',
      cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    }),
    columnHelper.accessor('status', { header: 'Status' }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const user = info.row.original;
        return (
          <div className="users-page__actions">
            <button onClick={() => navigate(`/admin/users/${user.id}`)}>View</button>
            {user.status === 'active' ? (
              <button onClick={() => handleBlock(user.id)}>Block</button>
            ) : user.status === 'blocked' ? (
              <button onClick={() => handleUnblock(user.id)}>Unblock</button>
            ) : null}
          </div>
        );
      },
    }),
  ];

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters };
    if (value) {
      newFilters[key] = value;
    } else {
      delete newFilters[key];
    }
    setFilters(newFilters);
    fetchUsers(1, newFilters);
  };

  return (
    <div className="users-page">
      <h1 className="users-page__title">Users</h1>

      <div className="users-page__filters">
        <select onChange={(e) => handleFilterChange('plan', e.target.value)} defaultValue="">
          <option value="">All Plans</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
        </select>
        <select onChange={(e) => handleFilterChange('activity', e.target.value)} defaultValue="">
          <option value="">All Activity</option>
          <option value="active">Active (30d)</option>
          <option value="inactive">Inactive</option>
        </select>
        <select onChange={(e) => handleFilterChange('hasShares', e.target.value)} defaultValue="">
          <option value="">All</option>
          <option value="true">Has Shares</option>
          <option value="false">No Shares</option>
        </select>
        <input
          type="text"
          placeholder="Search by email..."
          onChange={(e) => handleFilterChange('search', e.target.value)}
        />
        <label className="users-page__checkbox">
          <input
            type="checkbox"
            checked={showAnonymous}
            onChange={(e) => setShowAnonymous(e.target.checked)}
          />
          Show anonymous
        </label>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <AdminTable
          data={data}
          columns={columns}
          page={page}
          totalPages={totalPages}
          onPageChange={(p) => fetchUsers(p)}
          onSortChange={(sortBy, order) => fetchUsers(1, { ...filters, sortBy, order })}
        />
      )}
    </div>
  );
}
